import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, Check, X, MessageSquare, UserMinus } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "friends", label: "My Friends" },
  { id: "requests", label: "Requests" },
  { id: "find", label: "Find People" },
];

export default function Friends() {
  const [tab, setTab] = useState("friends");

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="label-mystic">Connections</div>
          <h1 className="font-serif text-5xl mt-2">Friends</h1>
        </div>
        <div className="flex gap-1 border-b border-secondary/15 overflow-x-auto" data-testid="friends-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.22em] whitespace-nowrap ${tab === t.id ? "text-secondary border-b-2 border-secondary" : "text-foreground/60 hover:text-foreground"}`}
              data-testid={`friends-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "friends" && <FriendList />}
        {tab === "requests" && <RequestList />}
        {tab === "find" && <FindPeople />}
      </div>
    </Layout>
  );
}

function FriendList() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  const load = async () => {
    const { data } = await api.get("/friends");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const unfriend = async (uid) => {
    if (!window.confirm("Remove this friend?")) return;
    try { await api.delete(`/friends/${uid}`); toast.success("Unfriended"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (rows.length === 0) {
    return <div className="parchment p-12 text-center text-foreground/60 italic font-serif text-xl">You haven't added any friends yet. Try "Find People".</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="friends-list">
      {rows.map((f) => (
        <div key={f.id} className="parchment p-4 flex items-center gap-3" data-testid={`friend-${f.otherUser.id}`}>
          <button onClick={() => nav(`/profile/${f.otherUser.id}`)} className="w-12 h-12 rounded-sm bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-xl text-secondary shrink-0">
            {f.otherUser.name?.[0]?.toUpperCase()}
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg truncate">{f.otherUser.name}</div>
            <div className="label-mystic">{f.otherUser.role}</div>
          </div>
          <button onClick={() => nav(`/messages/${f.otherUser.id}`)} title="Message"
            className="p-2 text-secondary/80 hover:text-secondary"
            data-testid={`friend-message-${f.otherUser.id}`}>
            <MessageSquare size={16} strokeWidth={1.5} />
          </button>
          <button onClick={() => unfriend(f.otherUser.id)} title="Unfriend"
            className="p-2 text-destructive/70 hover:text-destructive"
            data-testid={`friend-remove-${f.otherUser.id}`}>
            <UserMinus size={16} strokeWidth={1.5} />
          </button>
        </div>
      ))}
    </div>
  );
}

function RequestList() {
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/friends/requests", { params: { direction: "incoming" } }),
      api.get("/friends/requests", { params: { direction: "outgoing" } }),
    ]);
    setIncoming(a.data); setOutgoing(b.data);
  };
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    try { await api.post(`/friends/${id}/accept`); toast.success("Friend added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async (id) => {
    try { await api.post(`/friends/${id}/reject`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async (uid) => {
    try { await api.post(`/friends/cancel/${uid}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <section>
        <div className="label-mystic mb-3">Incoming · {incoming.length}</div>
        {incoming.length === 0 ? (
          <div className="parchment p-6 text-foreground/50 italic">No incoming requests.</div>
        ) : (
          <div className="parchment divide-y divide-secondary/15" data-testid="requests-incoming">
            {incoming.map((r) => (
              <div key={r.id} className="p-4 flex items-center gap-3" data-testid={`request-incoming-${r.id}`}>
                <div className="w-10 h-10 rounded-sm bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-secondary shrink-0">
                  {r.otherUser?.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif">{r.otherUser?.name}</div>
                  <div className="label-mystic">{r.otherUser?.role}</div>
                </div>
                <button onClick={() => accept(r.id)} className="bg-primary text-primary-foreground px-4 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid={`request-accept-${r.id}`}>
                  <Check size={12} strokeWidth={2} /> Accept
                </button>
                <button onClick={() => reject(r.id)} className="border border-destructive/60 text-destructive px-3 py-2 rounded-sm text-xs uppercase tracking-wider" data-testid={`request-reject-${r.id}`}>
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      <section>
        <div className="label-mystic mb-3">Outgoing · {outgoing.length}</div>
        {outgoing.length === 0 ? (
          <div className="parchment p-6 text-foreground/50 italic">No outgoing requests.</div>
        ) : (
          <div className="parchment divide-y divide-secondary/15" data-testid="requests-outgoing">
            {outgoing.map((r) => (
              <div key={r.id} className="p-4 flex items-center gap-3" data-testid={`request-outgoing-${r.id}`}>
                <div className="w-10 h-10 rounded-sm bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-secondary shrink-0">
                  {r.otherUser?.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif">{r.otherUser?.name}</div>
                  <div className="label-mystic">Awaiting response</div>
                </div>
                <button onClick={() => cancel(r.otherUser.id)} className="border border-secondary/40 text-foreground/80 px-3 py-2 rounded-sm text-xs uppercase tracking-wider" data-testid={`request-cancel-${r.otherUser.id}`}>
                  Cancel
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FindPeople() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [statusMap, setStatusMap] = useState({});

  const search = async () => {
    if (q.trim().length < 2) { setRows([]); return; }
    const { data } = await api.get("/users/search", { params: { q } });
    setRows(data);
    // hydrate statuses
    const map = {};
    await Promise.all(data.map(async (u) => {
      try { const { data: s } = await api.get(`/friends/status/${u.id}`); map[u.id] = s; }
      catch { /* noop */ }
    }));
    setStatusMap(map);
  };

  const sendReq = async (uid) => {
    try {
      await api.post("/friends/request", { toUserId: uid });
      toast.success("Friend request sent");
      const { data: s } = await api.get(`/friends/status/${uid}`);
      setStatusMap((m) => ({ ...m, [uid]: s }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="parchment p-3 flex items-center gap-2">
        <Search size={14} strokeWidth={1.5} className="text-foreground/50 ml-2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Search by name or email (min 2 chars)"
          className="flex-1 bg-transparent outline-none py-2"
          data-testid="find-people-input" />
        <button onClick={search} className="bg-primary text-primary-foreground px-4 py-2 rounded-sm text-xs uppercase tracking-wider" data-testid="find-people-btn">Search</button>
      </div>
      {rows.length === 0 ? (
        <div className="parchment p-6 text-foreground/50 italic">Search above to find people on Cauldron.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="find-people-results">
          {rows.map((u) => {
            const s = statusMap[u.id] || { status: "none" };
            return (
              <div key={u.id} className="parchment p-4 flex items-center gap-3" data-testid={`person-${u.id}`}>
                <div className="w-10 h-10 rounded-sm bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-secondary shrink-0">
                  {u.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif truncate">{u.name}</div>
                  <div className="label-mystic">{u.role}</div>
                </div>
                {s.status === "friends" && <span className="text-xs px-3 py-2 bg-secondary/20 text-secondary uppercase tracking-wider rounded-sm">Friends</span>}
                {s.status === "pending" && s.direction === "outgoing" && <span className="text-xs px-3 py-2 text-foreground/60 uppercase tracking-wider">Sent</span>}
                {s.status === "pending" && s.direction === "incoming" && <span className="text-xs px-3 py-2 text-primary uppercase tracking-wider">Pending</span>}
                {s.status === "none" && (
                  <button onClick={() => sendReq(u.id)} className="bg-primary text-primary-foreground px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid={`add-friend-${u.id}`}>
                    <UserPlus size={12} strokeWidth={2} /> Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
